import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Subject,
  takeUntil,
} from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { IIbanObject } from 'src/app/modules/athora/athora.constants';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { ITableKey } from 'src/app/modules/shared/components/basic-table/basic-table.constants';
import { isv_redeem_form_radio_PG } from 'src/app/modules/shared/components/total-redeem/total-redeem.constants';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { IPolicyAsset } from 'src/app/modules/shared/models/policy.model';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from './../../../../utils/CommonUtils';
import {
  isv_partial_redeem_form_keys,
  isv_partial_redeem_selective_radio,
  RISPA_ISP_ASSETS_TABLE_TABLE_KEYS,
} from './partial-redeem-isv.constants';

@Component({
  selector: 'app-partial-redeem-isv',
  templateUrl: './partial-redeem-isv.component.html',
  styleUrl: './partial-redeem-isv.component.scss',
})
export class PartialRedeemIsvComponent implements OnChanges, OnInit, OnDestroy {
  @Input() form: FormGroup;
  @Input() ibanList: IIbanObject[];
  @Input() assets: IPolicyAsset[];
  @Input() isReadOnly: boolean;
  @Input() redemptionTypes: string[];
  @Output() emitSelectedRedemptionType = new EventEmitter<{
    redemptionType: string;
    hasErrored: boolean;
    assets: IPolicyAsset[];
  }>();

  formKeys: IFormKey[] = CommonUtils.assign(isv_partial_redeem_form_keys);
  tableKeys: ITableKey[] = CommonUtils.assign(
    RISPA_ISP_ASSETS_TABLE_TABLE_KEYS
  );
  shouldWatchAmount: boolean = true;
  destroy$ = new Subject();

  constructor(
    private readonly _policyService: PolicyService,
    private readonly _spinnerService: SpinnerService,
    private readonly _alertService: AlertService
  ) {}

  ngOnInit(): void {
    const typeControl = this.form.get('type');
    const amountControl = this.form.get('amount');

    this.initializeRedemptionTypeWatcher(typeControl);
    this.checkInitialAmount(amountControl);
    this.initializeSingleRedemptionType();
    this.addPolicySpecificKeys();
    this.initializeAmountWatcher(amountControl);
  }

  private initializeRedemptionTypeWatcher(
    typeControl: AbstractControl | null
  ): void {
    if (
      ['Proporzionale', 'Selettivo'].every((v) => this.redemptionTypes.includes(v)) && typeControl
    ) {
      this.formKeys.push(isv_partial_redeem_selective_radio);

      typeControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.assets.forEach((fund) => (fund.amount = '0,00'));
          this.form.get('amount')?.setValue('0,00');
          this.shouldWatchAmount = !value;

          this.emitSelectedRedemptionType.emit({
            redemptionType: value ? 'Selettivo' : 'Proporzionale',
            hasErrored: false,
            assets: this.assets,
          });
        });
    }
  }

  private checkInitialAmount(amountControl: AbstractControl | null): void {
    if (!this.form.get('type')?.value && amountControl?.value) {
      const parsedValue = parseFloat(
        amountControl.value.replace(/\./g, '').replace(',', '.')
      );
      if (!isNaN(parsedValue) && parsedValue !== 0 && !this.isReadOnly) {
        this.retrieveFunds();
      }
    }
  }

  private initializeSingleRedemptionType(): void {
    if (this.redemptionTypes.length === 1) {
        if (this.redemptionTypes[0] === 'NonDefinito') {
            this.form.get('type')?.setValue(true);
        }
      const redemptionType = this.redemptionTypes[0];
      this.shouldWatchAmount = redemptionType === 'Proporzionale';
      this.emitSelectedRedemptionType.emit({
        redemptionType,
        hasErrored: false,
        assets: this.assets,
      });
    }
  }

  private addPolicySpecificKeys(): void {
    if (this._policyService.isLegalSubject) {
      this.formKeys.push(isv_redeem_form_radio_PG);
    }
  }

  private initializeAmountWatcher(amountControl: AbstractControl | null): void {
    amountControl?.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(
          () =>
            !this.isReadOnly &&
            this.shouldWatchAmount &&
            !!amountControl.value &&
            amountControl.value !== '0,00' &&
            !amountControl.disabled
        ),
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.isLimitExceeded) {
            this.retrieveFunds();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  retrieveFunds(): void {
    const contractNumber = this._policyService.contractNumber;
    const value = this.form.get('amount')?.value;
    const rawValue = value?.replace(/\./g, '')?.replace(',', '.');

    if (!contractNumber || !rawValue) return;
    this._spinnerService.show();

    this._policyService
      .retrieveFunds(contractNumber, this._policyService.operationId, rawValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (funds: any) => {
          this.assets = funds.activeFunds.map((fund) => ({
            ...fund,
            amount: this.formatCurrency(fund.amount),
            fundAmount: this.formatCurrency(fund.fundAmount),
          }));

          this.emitSelectedRedemptionType.emit({
            redemptionType: this.redemptionTypes[0],
            hasErrored: false,
            assets: this.assets,
          });
          this._spinnerService.hide();
        },
        error: (error) => this.handleRetrieveFundsError(error),
      });
  }

  private handleRetrieveFundsError(error: any): void {
    LoggerService.logError(this.constructor.name, 'retrieveFunds', { error });
    this._alertService.openErrorMessage(
      error.error?.response?.description ||
        'Errore in fase di recupero dei fondi',
      'retrieveFunds',
      error
    );
    this.assets.forEach((fund) => (fund.amount = '0,00'));
    this.emitSelectedRedemptionType.emit({
      redemptionType: this.redemptionTypes[0],
      hasErrored: true,
      assets: this.assets,
    });
    this._spinnerService.hide();
  }

  private formatCurrency(value: string): string {
    return parseFloat(value).toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ibanList'] && changes['ibanList'].currentValue) {
      this.populateIbanOptions();
    }
  }

  get showAssets(): boolean {
    return this.assets && this.assets.length > 0;
  }

  get isLimitExceeded(): boolean {
    const totalFundAmount = this.assets.reduce(
      (acc, asset) =>
        acc +
        (parseFloat(asset.fundAmount.replace(/\./g, '').replace(',', '.')) ||
          0),
      0
    );
    const inputAmount = parseFloat(
      (this.form.get('amount')?.value || '0')
        .replace(/\./g, '')
        .replace(',', '.')
    );
    return totalFundAmount < inputAmount;
  }

  populateIbanOptions(): void {
    this.formKeys = CommonUtils.populateIbanOptions(
      this.formKeys,
      this.ibanList
    );
  }
}
